import type { UserContextEvent } from '../telemetry-models';

/**
 * Device information including hardware, browser, OS, and platform details
 */
export type DeviceInfo = Pick<
  UserContextEvent,
  | 'cpu'
  | 'memory'
  | 'max_touch_points'
  | 'platform'
  | 'input_touch'
  | 'input_pointer'
  | 'input_hover'
  | 'browser_name'
  | 'browser_version'
  | 'is_web_view'
  | 'os_name'
  | 'os_version'
>;

/**
 * Detects device hardware, browser, OS, and input capabilities.
 */
export class DeviceDetector {
  static detect(): DeviceInfo {
    const platform = this._detectPlatformType();
    const inputTouch = 'ontouchstart' in window;
    const inputPointer = this._detectPointerType();
    const inputHover = this._detectHoverCapability();
    const cpu = navigator.hardwareConcurrency;

    let memory: number | undefined;
    if (
      'deviceMemory' in navigator &&
      typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
    ) {
      memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    }

    const maxTouchPoints = typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : undefined;
    const browser = this._detectBrowser();
    const os = this._detectOS();
    const isWebView = this._detectWebView();

    return {
      cpu,
      memory,
      max_touch_points: maxTouchPoints,
      platform,
      input_touch: inputTouch,
      input_pointer: inputPointer,
      input_hover: inputHover,
      browser_name: browser.name,
      browser_version: browser.version,
      is_web_view: isWebView,
      os_name: os.name,
      os_version: os.version,
    };
  }

  private static _detectPlatformType(): 'desktop' | 'mobile' | 'tablet' {
    const width = window.innerWidth;
    const isTouchDevice = 'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);

    if (isTouchDevice) {
      return width < 768 ? 'mobile' : 'tablet';
    }
    return 'desktop';
  }

  private static _detectPointerType(): 'coarse' | 'fine' | 'none' {
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return 'coarse';
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) return 'fine';
    return 'none';
  }

  private static _detectHoverCapability(): boolean {
    return window.matchMedia ? window.matchMedia('(hover: hover)').matches : false;
  }

  /**
   * Detect browser name and version
   */
  private static _detectBrowser(): { name: string; version: string } {
    const ua = navigator.userAgent;
    let name = 'Unknown';
    let version = 'Unknown';

    // Edge (Chromium-based)
    if (ua.includes('Edg/')) {
      name = 'Edge';
      const match = ua.match(/Edg\/(\d+\.\d+)/);
      if (match) version = match[1];
    }
    // Chrome
    else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
      name = 'Chrome';
      const match = ua.match(/Chrome\/(\d+\.\d+)/);
      if (match) version = match[1];
    }
    // Safari
    else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
      name = 'Safari';
      const match = ua.match(/Version\/(\d+\.\d+)/);
      if (match) version = match[1];
    }
    // Firefox
    else if (ua.includes('Firefox/')) {
      name = 'Firefox';
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      if (match) version = match[1];
    }
    // Opera
    else if (ua.includes('OPR/')) {
      name = 'Opera';
      const match = ua.match(/OPR\/(\d+\.\d+)/);
      if (match) version = match[1];
    }

    return { name, version };
  }

  /**
   * Detect operating system name and version
   */
  private static _detectOS(): { name: string; version?: string } {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    let name = 'Unknown';
    let version: string | undefined;

    // Windows
    if (ua.includes('Windows')) {
      name = 'Windows';
      if (ua.includes('Windows NT 10.0')) version = '10';
      else if (ua.includes('Windows NT 6.3')) version = '8.1';
      else if (ua.includes('Windows NT 6.2')) version = '8';
      else if (ua.includes('Windows NT 6.1')) version = '7';
    }
    // macOS
    else if (ua.includes('Mac OS X')) {
      name = 'macOS';
      // eslint-disable-next-line security/detect-unsafe-regex
      const match = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/);
      if (match) {
        version = match[1].replace(/_/g, '.');
      }
    }
    // iOS
    else if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) {
      name = 'iOS';
      // eslint-disable-next-line security/detect-unsafe-regex
      const match = ua.match(/OS (\d+[._]\d+(?:[._]\d+)?)/);
      if (match) {
        version = match[1].replace(/_/g, '.');
      }
    }
    // Android
    else if (ua.includes('Android')) {
      name = 'Android';
      const match = ua.match(/Android (\d+\.\d+)/);
      if (match) version = match[1];
    }
    // Linux
    else if (ua.includes('Linux') || platform.includes('Linux')) {
      name = 'Linux';
    }
    // ChromeOS
    else if (ua.includes('CrOS')) {
      name = 'ChromeOS';
    }

    return { name, version };
  }

  /**
   * Detect if running in a WebView
   */
  private static _detectWebView(): boolean {
    const ua = navigator.userAgent;

    // iOS WebView detection
    // WKWebView: doesn't have 'Safari' in UA but has 'iPhone/iPad' and 'AppleWebKit'
    const isIOSWebView =
      (ua.includes('iPhone') || ua.includes('iPad')) && ua.includes('AppleWebKit') && !ua.includes('Safari/');

    // Android WebView detection
    // Android WebView has 'wv' in the UA string
    const isAndroidWebView = ua.includes('Android') && ua.includes('wv');

    // Facebook/Instagram in-app browser
    const isFacebookWebView = ua.includes('FBAN') || ua.includes('FBAV') || ua.includes('Instagram');

    // Line, WeChat, etc.
    const isOtherWebView = ua.includes('Line/') || ua.includes('MicroMessenger/');

    return isIOSWebView || isAndroidWebView || isFacebookWebView || isOtherWebView;
  }
}
